import React from 'react';
import {
  branch,
  compose,
  renderComponent,
  renderNothing,
  withHandlers,
  withState,
} from 'recompose';
import {
  CHANGE_MSG,
  CHANGE_PAYMENT_METHOD_MSG,
  INVALID_BRAINTREE_INSTANCE_MSG,
  SUCESSFULLY_CHANGE_PAYMENT_METHOD_MSG,
} from '../../../constants/localization';
import CustomDropIn from '../Payment/CustomDropIn';
import { Dropin, PaymentMethodPayload } from 'braintree-web-drop-in';
import { graphql } from 'react-apollo';
import { OptionProps } from '@apollo/react-hoc/lib/types';
import logError from '../../../utils/logError';
import { toast } from 'react-toastify';
import { tearDownDropInInstance } from '../../../constants/payment';
import { messageFromError } from '../../../constants/ui';
import { SubscriptionType } from '../../../constants/types';
import Loading from '../../Loading';
import { UPDATE_PAYMENT_SUBSCRIPTIONS } from '../../../constants/gql/payment';
import ModalActionableBase from '../ModalActionableBase';

const ChangePaymentMethodModal: React.FunctionComponent<IProps> = ({
  isOpen,
  onCloseModal,
  setDropInInstance,
  isDisabled,
  updatePaymentSubscription,
  dropInInstance,
  errorMessage,
}) => (
  <ModalActionableBase
    isOpen={isOpen}
    onCloseModal={onCloseModal}
    confirmButtonId={'submit-button'}
    confirmAction={updatePaymentSubscription}
    confirmText={CHANGE_MSG}
    loading={isDisabled}
    isHideConfirmButton={!dropInInstance}
    title={CHANGE_PAYMENT_METHOD_MSG}
  >
    <CustomDropIn
      setDropInInstance={setDropInInstance}
      errorMessage={errorMessage}
    />
  </ModalActionableBase>
);

export default compose<IInnerProps, IOuterProps>(
  branch(({ isOpen }: IProps) => !isOpen, renderNothing),
  branch(
    ({ activeSubscriptions }: IProps) => !activeSubscriptions,
    renderComponent(Loading),
  ),
  withState('dropInInstance', 'setDropInInstance', null),
  withState('isDisabled', 'setDisabled', false),
  withState('errorMessage', 'setErrorMessage', null),
  withHandlers({
    onCloseModal: ({
      dropInInstance,
      setDropInInstance,
      toggleModal,
      isDisabled,
    }: IProps) => () => {
      if (isDisabled) {
        return;
      }
      if (dropInInstance) {
        tearDownDropInInstance(dropInInstance, () => setDropInInstance(null));
      }
      toggleModal(false);
    },
  }),
  graphql(UPDATE_PAYMENT_SUBSCRIPTIONS, {
    options: { refetchQueries: ['me'] },
    props: ({ mutate, ownProps }: OptionProps<IProps>) => ({
      updatePaymentSubscription: async (): Promise<void> => {
        const {
          dropInInstance,
          setDisabled,
          onCloseModal,
          setErrorMessage,
        } = ownProps;
        setDisabled(true);
        if (!dropInInstance) {
          setErrorMessage(INVALID_BRAINTREE_INSTANCE_MSG);
          setDisabled(false);
          return;
        }
        return dropInInstance
          .requestPaymentMethod()
          .then((paymentMethod: PaymentMethodPayload) =>
            mutate({
              variables: { nonce: paymentMethod.nonce },
            }),
          )
          .then((_) => {
            setErrorMessage(null);
            toast(SUCESSFULLY_CHANGE_PAYMENT_METHOD_MSG, { type: 'info' });
            setDisabled(false);
            onCloseModal();
          })
          .catch((error: Error) => {
            logError('FAILED CHANGE PAYMENT METHOD', error);
            setErrorMessage(messageFromError(error));
            setDisabled(false);
          });
      },
    }),
  }),
)(ChangePaymentMethodModal);

type IProps = IInnerProps & IOuterProps;

interface IInnerProps {
  dropInInstance: Dropin;
  setDropInInstance: (value: Dropin) => void;
  isDisabled: boolean;
  setDisabled: (value: boolean) => void;
  onCloseModal: () => void;
  updatePaymentSubscription: () => Promise<void>;
  errorMessage: string | null;
  setErrorMessage: (value: string) => void;
}

interface IOuterProps {
  isOpen: boolean;
  toggleModal: (value: boolean) => void;
  activeSubscriptions: SubscriptionType[];
}
